// Split from djValueAdd.ts (I2). Shared local runtime helpers kept per-file for isolation.
import { SeqCounter } from "@djsem/shared";

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();
import { DJSEMTransitionRiskAnalyzer } from "./transitionAssistant";

// ============================================================================
// PROMPT 40 — AI Set Coach
// ============================================================================

/** A dimension of set quality the AI Set Coach evaluates (flow, difficulty, readiness, etc.). */
export type DJSEMSetCoachDimension =
  | "energy_flow" | "bpm_flow" | "key_flow" | "genre_flow" | "mood_flow"
  | "vocal_density" | "transition_difficulty" | "opener_strength" | "closer_strength"
  | "peak_timing" | "repetition" | "metadata_confidence"
  | "event_fit" | "visual_readiness" | "show_pack_readiness";

/** A single detected problem in a set along one coaching dimension, with supporting evidence. */
export interface DJSEMSetCoachIssue {
  id: string;
  setId: string;
  dimension: DJSEMSetCoachDimension;
  severity: "info" | "warning" | "high" | "critical";
  trackId?: string;
  fromTrackId?: string; toTrackId?: string;
  startIndex?: number; endIndex?: number;
  title: string;
  explanation: string;
  evidence: string[];
  confidence: number;
}

/** A suggested fix or improvement arising from set coach analysis. */
export interface DJSEMSetCoachSuggestion {
  id: string;
  suggestionType: string;
  title: string;
  explanation: string;
  affectedTrackIds: string[];
  safeToAutoApply: boolean;
  requiresUserConfirmation: boolean;
  confidence: number;
  warnings: string[];
}

/** Overall and per-dimension quality score for a set, each with its own confidence and warnings. */
export interface DJSEMSetCoachScore {
  overallScore: number;
  confidence: number;
  dimensionScores: Partial<Record<DJSEMSetCoachDimension, { score: number; confidence: number; warnings: string[]; }>>;
}

/** The full result of running the AI Set Coach against a set: score, strengths, issues, and suggestions. */
export interface DJSEMSetCoachReport {
  id: string;
  setId: string;
  score: DJSEMSetCoachScore;
  summary: string;
  strengths: string[];
  issues: DJSEMSetCoachIssue[];
  suggestions: DJSEMSetCoachSuggestion[];
  createdAt: string;
  warnings: string[];
}

/** Minimal per-track metadata needed to run set coach analysis. */
export interface SetCoachTrackInput {
  id: string; title?: string; artist?: string;
  bpm?: number; key?: string; energy?: number;
  genre?: string; mood?: string;
  hasVocals?: boolean;
  hasBrandWorld?: boolean; hasVisualScene?: boolean;
}

/** Analyses a DJ set's track order for flow, transition difficulty, and readiness issues. */
export class DJSEMSetCoachService {
  /**
   * Runs the full set coach analysis over an ordered list of tracks, detecting BPM/key/energy
   * flow issues, transition difficulty, repetition, and metadata gaps, then scores the set.
   * @param setId - id of the set being analysed (attached to each issue for traceability).
   * @param tracks - the ordered tracks in the set.
   * @returns a coaching report with overall score, strengths, issues, and suggestions.
   */
  analyseSet(setId: string, tracks: SetCoachTrackInput[]): DJSEMSetCoachReport {
    const issues: DJSEMSetCoachIssue[] = [];
    const strengths: string[] = [];

    if (tracks.length === 0) {
      issues.push({ id: `sci_${_seq.next()}`, setId, dimension: "energy_flow", severity: "critical", title: "Empty set", explanation: "Set has no tracks", evidence: [], confidence: 1 });
      return { id: `scr_${_seq.next()}`, setId, score: { overallScore: 0, confidence: 1, dimensionScores: {} }, summary: "Empty set — add tracks to analyse.", strengths: [], issues, suggestions: [], createdAt: AT, warnings: [] };
    }

    if (tracks.length === 1) {
      return { id: `scr_${_seq.next()}`, setId, score: { overallScore: 0.5, confidence: 0.3, dimensionScores: {} }, summary: "Single-track set — limited analysis possible.", strengths: ["Track selected."], issues: [], suggestions: [], createdAt: AT, warnings: ["Single-track set — flow analysis requires 2+ tracks."] };
    }

    // BPM flow
    for (let i = 0; i < tracks.length - 1; i++) {
      const a = tracks[i], b = tracks[i + 1];
      if (a.bpm && b.bpm && Math.abs(a.bpm - b.bpm) > 10) {
        issues.push({ id: `sci_${_seq.next()}`, setId, dimension: "bpm_flow", severity: Math.abs(a.bpm - b.bpm) > 20 ? "high" : "warning", fromTrackId: a.id, toTrackId: b.id, startIndex: i, endIndex: i + 1, title: `BPM jump: ${a.bpm}→${b.bpm}`, explanation: `${Math.abs(a.bpm - b.bpm)} BPM difference between track ${i + 1} and ${i + 2}`, evidence: [`${a.title ?? a.id}: ${a.bpm}`, `${b.title ?? b.id}: ${b.bpm}`], confidence: 0.9 });
      }
    }

    // Key flow
    for (let i = 0; i < tracks.length - 1; i++) {
      const a = tracks[i], b = tracks[i + 1];
      if (a.key && b.key && a.key !== b.key) {
        issues.push({ id: `sci_${_seq.next()}`, setId, dimension: "key_flow", severity: "info", fromTrackId: a.id, toTrackId: b.id, title: `Key change: ${a.key}→${b.key}`, explanation: "Key changes between adjacent tracks", evidence: [], confidence: 0.5 });
      }
    }

    // Energy dead zone (3+ consecutive low-energy tracks)
    let lowRun = 0;
    for (let i = 0; i < tracks.length; i++) {
      if (tracks[i].energy !== undefined && tracks[i].energy! < 0.3) { lowRun++; }
      else {
        if (lowRun >= 3) issues.push({ id: `sci_${_seq.next()}`, setId, dimension: "energy_flow", severity: "warning", startIndex: i - lowRun, endIndex: i - 1, title: "Energy dead zone", explanation: `${lowRun} consecutive low-energy tracks`, evidence: [], confidence: 0.7 });
        lowRun = 0;
      }
    }
    if (lowRun >= 3) issues.push({ id: `sci_${_seq.next()}`, setId, dimension: "energy_flow", severity: "warning", startIndex: tracks.length - lowRun, endIndex: tracks.length - 1, title: "Energy dead zone", explanation: `${lowRun} consecutive low-energy tracks at end`, evidence: [], confidence: 0.7 });

    // Opener / closer
    const opener = tracks[0];
    if (opener.energy !== undefined && opener.energy > 0.8) issues.push({ id: `sci_${_seq.next()}`, setId, dimension: "opener_strength", severity: "warning", trackId: opener.id, title: "High-energy opener", explanation: "Opening track has very high energy — consider a warmup track", evidence: [`energy: ${opener.energy}`], confidence: 0.6 });
    const closer = tracks[tracks.length - 1];
    if (closer.energy !== undefined && closer.energy > 0.9) issues.push({ id: `sci_${_seq.next()}`, setId, dimension: "closer_strength", severity: "info", trackId: closer.id, title: "Very high energy closer", explanation: "Closing at peak energy — intentional or needs resolution?", evidence: [], confidence: 0.5 });

    // Repetition (same artist back-to-back)
    for (let i = 0; i < tracks.length - 1; i++) {
      if (tracks[i].artist && tracks[i + 1].artist && tracks[i].artist === tracks[i + 1].artist) {
        issues.push({ id: `sci_${_seq.next()}`, setId, dimension: "repetition", severity: "warning", fromTrackId: tracks[i].id, toTrackId: tracks[i + 1].id, title: `Same artist back-to-back: ${tracks[i].artist}`, explanation: "Playing same artist consecutively may feel repetitive", evidence: [], confidence: 0.8 });
      }
    }

    // Metadata confidence
    const missingBpm = tracks.filter(t => !t.bpm).length;
    const missingKey = tracks.filter(t => !t.key).length;
    if (missingBpm > 0 || missingKey > 0) issues.push({ id: `sci_${_seq.next()}`, setId, dimension: "metadata_confidence", severity: "warning", title: "Incomplete metadata", explanation: `${missingBpm} track(s) missing BPM, ${missingKey} missing key`, evidence: [], confidence: 1 });

    // Visual readiness
    const noVisuals = tracks.filter(t => !t.hasBrandWorld && !t.hasVisualScene).length;
    if (noVisuals === tracks.length) issues.push({ id: `sci_${_seq.next()}`, setId, dimension: "visual_readiness", severity: "info", title: "No visual assets", explanation: "No Brand World or visual scenes linked", evidence: [], confidence: 1 });

    // Transition difficulty (F3): run the Transition Risk Analyzer across adjacent pairs
    // so transition_difficulty carries real per-pair evidence rather than being unpopulated.
    const transitionAnalyzer = new DJSEMTransitionRiskAnalyzer();
    for (let i = 0; i < tracks.length - 1; i++) {
      const a = tracks[i], b = tracks[i + 1];
      const report = transitionAnalyzer.analysePair({
        fromTrack: { id: a.id, bpm: a.bpm, key: a.key, energy: a.energy, hasVocals: a.hasVocals },
        toTrack: { id: b.id, bpm: b.bpm, key: b.key, energy: b.energy, hasVocals: b.hasVocals },
      });
      // Only surface genuinely difficult transitions (low compatibility) to avoid noise.
      if (report.compatibilityScore < 0.5) {
        const worst = report.issues.sort((x, y) => (y.severity === "high" || y.severity === "critical" ? 1 : 0) - (x.severity === "high" || x.severity === "critical" ? 1 : 0))[0];
        issues.push({
          id: `sci_${_seq.next()}`, setId, dimension: "transition_difficulty",
          severity: report.compatibilityScore < 0.3 ? "high" : "warning",
          fromTrackId: a.id, toTrackId: b.id, startIndex: i, endIndex: i + 1,
          title: `Difficult transition (${(report.compatibilityScore * 100).toFixed(0)}% compatible)`,
          explanation: worst ? worst.explanation : "Adjacent tracks are hard to mix cleanly.",
          evidence: report.issues.map(iss => iss.explanation),
          confidence: report.compatibilityConfidence,
        });
      }
    }

    // Strengths
    if (issues.filter(i => i.dimension === "bpm_flow").length === 0 && tracks.every(t => t.bpm)) strengths.push("Smooth BPM flow throughout the set.");
    if (tracks.length >= 5) strengths.push(`Set has ${tracks.length} tracks — good depth.`);

    // Score
    const penaltyPerHigh = 0.15;
    const penaltyPerWarn = 0.05;
    const highCount = issues.filter(i => i.severity === "high" || i.severity === "critical").length;
    const warnCount = issues.filter(i => i.severity === "warning").length;
    const overallScore = Math.max(0, Math.min(1, 1 - highCount * penaltyPerHigh - warnCount * penaltyPerWarn));
    const confidence = tracks.every(t => t.bpm && t.key && t.energy !== undefined) ? 0.8 : 0.4;

    // Suggestions
    const suggestions: DJSEMSetCoachSuggestion[] = [];
    if (missingBpm > 0) suggestions.push({ id: `scs_${_seq.next()}`, suggestionType: "review_metadata", title: "Review missing metadata", explanation: `${missingBpm} tracks need BPM data`, affectedTrackIds: tracks.filter(t => !t.bpm).map(t => t.id), safeToAutoApply: false, requiresUserConfirmation: true, confidence: 1, warnings: [] });

    return {
      id: `scr_${_seq.next()}`, setId,
      score: { overallScore, confidence, dimensionScores: {} },
      summary: `Set scored ${(overallScore * 100).toFixed(0)}% with ${issues.length} issue(s) and ${strengths.length} strength(s).`,
      strengths, issues, suggestions, createdAt: AT, warnings: [],
    };
  }
}
